import React, { useState } from "react";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Calendar } from "@/components/ui/calendar";
import { Button } from "@/components/ui/button";
import { Calendar as CalendarIcon } from "lucide-react";
import { fmtDate } from "@/lib/api";
import { cn } from "@/lib/utils";

// Convert ISO YYYY-MM-DD to a local Date at 00:00 (no timezone shift)
function isoToDate(iso) {
  if (!iso || typeof iso !== "string") return undefined;
  const m = iso.slice(0, 10).match(/^(\d{4})-(\d{2})-(\d{2})$/);
  if (!m) return undefined;
  return new Date(Number(m[1]), Number(m[2]) - 1, Number(m[3]));
}
function dateToIso(d) {
  if (!d) return "";
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const dd = String(d.getDate()).padStart(2, "0");
  return `${y}-${m}-${dd}`;
}

/**
 * Mini calendar date picker. Displays DD-MM-YYYY, stores ISO YYYY-MM-DD.
 * Props: value (ISO), onChange(iso), testid, disabled, minDate (ISO), placeholder
 */
export default function DateDMY({ value, onChange, testid, disabled, minDate, placeholder = "DD-MM-YYYY" }) {
  const [open, setOpen] = useState(false);
  const selected = isoToDate(value);
  const minD = isoToDate(minDate);

  const handleSelect = (d) => {
    if (!d) return;
    onChange && onChange(dateToIso(d));
    setOpen(false);
  };
  const goToday = () => {
    const t = new Date();
    onChange && onChange(dateToIso(t));
    setOpen(false);
  };

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          type="button"
          variant="outline"
          disabled={disabled}
          data-testid={testid}
          className={cn("w-full justify-between font-normal", !value && "text-slate-500")}
        >
          <span>{value ? fmtDate(value) : placeholder}</span>
          <CalendarIcon className="ml-2 h-4 w-4 opacity-60" />
        </Button>
      </PopoverTrigger>
      <PopoverContent className="p-0 w-auto" align="start">
        <Calendar
          mode="single"
          selected={selected}
          defaultMonth={selected || new Date()}
          onSelect={handleSelect}
          disabled={minD ? { before: minD } : undefined}
          initialFocus
        />
        <div className="flex justify-between items-center border-t border-slate-200 px-3 py-2 bg-slate-50">
          <Button type="button" variant="ghost" size="sm" onClick={goToday} data-testid={testid ? `${testid}-today` : undefined}>Today</Button>
          <Button type="button" variant="ghost" size="sm" onClick={() => { onChange && onChange(""); setOpen(false); }}>Clear</Button>
        </div>
      </PopoverContent>
    </Popover>
  );
}
